// Minimal Ethereum layer for the Internet Casino — no dependencies, on purpose.
//
// Two reasons this is hand-rolled instead of viem/ethers:
//   1. This bundle is Create React App 5 on React 16. Modern web3 libraries drag in a webpack-5
//      polyfill fight that has nothing to do with shipping a game.
//   2. Babel already broke this site once by rewriting `BigInt(10) ** BigInt(18)` into
//      `Math.pow(BigInt, BigInt)`, a runtime TypeError that blanked the entire desktop
//      (2026-09-04). The exponent operator is the one BigInt operation the transform touches.
//      NOTHING in this file uses `**` on a BigInt, and the test suite greps the built bundle
//      for `Math.pow(BigInt` to keep it that way.
//
// Everything here is read-or-sign only. This file never sees a private key and never can.

export const CHAIN_ID = 4663;
export const CHAIN_ID_HEX = '0x1237';
export const RPC_URL = 'https://rpc.mainnet.chain.robinhood.com';
export const EXPLORER = 'https://explorer.chain.robinhood.com';

export const CHAIN_PARAMS = {
  chainId: CHAIN_ID_HEX,
  chainName: 'Robinhood Chain',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: [RPC_URL],
  blockExplorerUrls: [EXPLORER],
};

// ── numbers ──────────────────────────────────────────────────────────────────

/** 10n ** BigInt(n), written as a loop because `**` on BigInt is the transform that broke us. */
export function pow10(n) {
  let out = BigInt(1);
  for (let i = 0; i < n; i += 1) out *= BigInt(10);
  return out;
}

export const WAD = pow10(18);

/** Parse a human amount ("1.25") into 18-decimal wei without ever touching a float. */
export function parseUnits(value, decimals) {
  const dec = decimals === undefined ? 18 : decimals;
  const s = String(value == null ? '' : value).trim();
  if (!s || !/^\d*\.?\d*$/.test(s)) return BigInt(0);
  const [whole, frac = ''] = s.split('.');
  const padded = (frac + '0'.repeat(dec)).slice(0, dec);
  return BigInt(whole || '0') * pow10(dec) + BigInt(padded || '0');
}

/** Format wei for display. `places` is how many decimals to show, not how many exist. */
export function formatUnits(value, decimals, places) {
  const dec = decimals === undefined ? 18 : decimals;
  const p = places === undefined ? 4 : places;
  const v = BigInt(value || 0);
  const base = pow10(dec);
  const neg = v < BigInt(0);
  const abs = neg ? -v : v;
  const whole = abs / base;
  const frac = (abs % base).toString().padStart(dec, '0').slice(0, p);
  const body = p > 0 ? `${whole}.${frac}` : String(whole);
  return neg ? `-${body}` : body;
}

// ── ABI codec ────────────────────────────────────────────────────────────────
// Only the types the cabinets actually use. Anything else throws loudly rather than
// silently encoding garbage into a transaction.

const strip = h => (h || '').replace(/^0x/, '');

function pad32(hex) {
  const s = strip(hex);
  if (s.length > 64) throw new Error('word overflow');
  return s.padStart(64, '0');
}

function uintToHex(v) {
  const n = BigInt(v);
  if (n < BigInt(0)) throw new Error('negative uint');
  return pad32(n.toString(16));
}

function isDynamic(type) {
  return type === 'bytes' || type === 'string' || type.endsWith('[]');
}

function encodeOne(type, value) {
  if (type === 'address') return pad32(String(value).toLowerCase());
  if (type === 'bool') return pad32(value ? '1' : '0');
  if (type === 'bytes32') return pad32(value);
  if (/^uint\d*$/.test(type) || /^int\d*$/.test(type)) return uintToHex(value);
  if (type === 'bytes') {
    const body = strip(value);
    if (body.length % 2) throw new Error('odd-length bytes');
    const words = body.padEnd(Math.ceil(body.length / 64) * 64, '0');
    return uintToHex(body.length / 2) + words;
  }
  throw new Error(`unsupported abi type: ${type}`);
}

/** Head/tail encoding for a function's arguments. */
export function encodeArgs(types, values) {
  let head = '';
  let tail = '';
  const headWords = types.length;
  types.forEach((t, i) => {
    if (isDynamic(t)) {
      const offset = headWords * 32 + tail.length / 2;
      head += uintToHex(offset);
      tail += encodeOne(t, values[i]);
    } else {
      head += encodeOne(t, values[i]);
    }
  });
  return head + tail;
}

export function encodeCall(fn, values) {
  return fn.sel + encodeArgs(fn.inputs, values || []);
}

function word(data, index) {
  return data.slice(index * 64, index * 64 + 64);
}

function decodeOne(type, data, index) {
  const w = word(data, index);
  if (type === 'address') return '0x' + w.slice(24);
  if (type === 'bool') return BigInt('0x' + w) !== BigInt(0);
  if (type === 'bytes32') return '0x' + w;
  if (/^uint\d*$/.test(type)) return BigInt('0x' + w);
  if (/^int\d*$/.test(type)) {
    const v = BigInt('0x' + w);
    const half = BigInt('0x8000000000000000000000000000000000000000000000000000000000000000');
    return v >= half ? v - half * BigInt(2) : v;
  }
  if (type === 'bytes' || type === 'string') {
    const off = Number(BigInt('0x' + w)) * 2;
    const len = Number(BigInt('0x' + data.slice(off, off + 64)));
    const body = data.slice(off + 64, off + 64 + len * 2);
    if (type === 'bytes') return '0x' + body;
    return decodeUtf8(body);
  }
  const arr = /^(.+)\[(\d*)\]$/.exec(type);
  if (arr) {
    const inner = arr[1];
    if (arr[2]) {
      const n = Number(arr[2]);
      const out = [];
      for (let i = 0; i < n; i += 1) out.push(decodeOne(inner, data, index + i));
      return out;
    }
    const off = Number(BigInt('0x' + w)) * 2;
    const n = Number(BigInt('0x' + data.slice(off, off + 64)));
    const body = data.slice(off + 64);
    const out = [];
    for (let i = 0; i < n; i += 1) out.push(decodeOne(inner, body, i));
    return out;
  }
  throw new Error(`unsupported abi type: ${type}`);
}

function decodeUtf8(hexBody) {
  let s = '';
  for (let i = 0; i < hexBody.length; i += 2) {
    s += String.fromCharCode(parseInt(hexBody.substr(i, 2), 16));
  }
  return decodeURIComponent(escape(s));
}

/** Decode a return payload. Fixed-size types only at the top level, plus bytes/string/arrays. */
export function decodeResult(types, data) {
  const body = strip(data);
  // A static head slot per output; dynamic ones carry an offset into the same body.
  const out = types.map((t, i) => decodeOne(t, body, i));
  return out.length === 1 ? out[0] : out;
}

// ── provider ─────────────────────────────────────────────────────────────────

const discovered = [];

if (typeof window !== 'undefined') {
  // EIP-6963: wallets announce themselves rather than fighting over window.ethereum.
  window.addEventListener('eip6963:announceProvider', event => {
    const detail = event && event.detail;
    if (!detail || !detail.provider) return;
    if (discovered.some(p => p.info && detail.info && p.info.uuid === detail.info.uuid)) return;
    discovered.push(detail);
  });
  try {
    window.dispatchEvent(new Event('eip6963:requestProvider'));
  } catch (e) {
    /* older browsers: fall through to window.ethereum */
  }
}

export function wallets() {
  const list = discovered.slice();
  if (!list.length && typeof window !== 'undefined' && window.ethereum) {
    list.push({ info: { name: 'Browser wallet', uuid: 'injected', rdns: 'injected' }, provider: window.ethereum });
  }
  return list;
}

/** Re-ask. A wallet that injected after first paint only answers a fresh request event. */
export function requestWallets() {
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new Event('eip6963:requestProvider'));
    } catch (e) {
      /* older browsers: wallets() still falls back to window.ethereum */
    }
  }
  return wallets();
}

const REMEMBER_KEY = 'ic-casino-wallet';

function rememberedRdns() {
  try {
    return window.localStorage.getItem(REMEMBER_KEY) || null;
  } catch (e) {
    return null; // private window, or storage blocked
  }
}

function remember(rdns) {
  try {
    if (rdns) window.localStorage.setItem(REMEMBER_KEY, rdns);
    else window.localStorage.removeItem(REMEMBER_KEY);
  } catch (e) {
    /* not being able to remember is not a reason to fail the connect */
  }
}

export function hasWallet() {
  return wallets().length > 0;
}

let active = null;

export function activeProvider() {
  if (active) return active;
  const list = wallets();
  if (!list.length) return null;

  const rdns = rememberedRdns();
  if (rdns) {
    const hit = list.find(w => w.info && w.info.rdns === rdns);
    if (hit) {
      active = hit.provider;
      return active;
    }
  }

  // EIP-6963 announcement order is a race, so list[0] is whichever wallet shouted first.
  // Picking it for the player is exactly how you end up in Phantom when you meant MetaMask.
  // With a real choice on offer, make the player make it.
  return list.length === 1 ? list[0].provider : null;
}

/** Choose by uuid or rdns, and remember it across reloads. */
export function selectWallet(id) {
  const found = wallets().find(w => w.info && (w.info.uuid === id || w.info.rdns === id));
  active = found ? found.provider : null;
  if (found && found.info) remember(found.info.rdns || found.info.uuid);
  return active;
}

/** Drop the remembered wallet so the next connect asks again. */
export function forgetWallet() {
  active = null;
  remember(null);
}

/** A public RPC call that hangs is worse than one that fails: callers retry a failure, nobody retries a hang. */
const RPC_TIMEOUT_MS = 10000;
const rpcTimeout = () =>
  typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(RPC_TIMEOUT_MS)
    : undefined;

/** A read that never needs a wallet: the public RPC answers it. */
export async function rpc(method, params) {
  const res = await fetch(RPC_URL, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', id: Date.now(), method, params: params || [] }),
    signal: rpcTimeout(),
  });
  const json = await res.json();
  if (json.error) throw new Error(json.error.message || 'rpc error');
  return json.result;
}

export async function readCall(to, data) {
  return rpc('eth_call', [{ to, data }, 'latest']);
}

/** Read a contract function through the public RPC. No wallet, no prompt. */
export async function read(to, fn, args) {
  const data = await readCall(to, encodeCall(fn, args));
  if (!fn.outputs.length) return null;
  return decodeResult(fn.outputs, data);
}

export async function connect(id) {
  if (id) selectWallet(id);
  const provider = activeProvider();
  if (!provider) {
    // Two different failures, and the UI needs to tell them apart: nothing installed is a
    // dead end, several installed is a question.
    const list = wallets();
    const err = new Error(
      list.length ? 'Choose which wallet to connect.' : 'No wallet found. Install MetaMask or Rabby.',
    );
    err.code = list.length ? 'CHOOSE_WALLET' : 'NO_WALLET';
    throw err;
  }
  const accounts = await provider.request({ method: 'eth_requestAccounts' });
  await ensureChain(provider);
  return accounts[0];
}

export async function currentAccount() {
  const provider = activeProvider();
  if (!provider) return null;
  const accounts = await provider.request({ method: 'eth_accounts' });
  return accounts && accounts.length ? accounts[0] : null;
}

export async function ensureChain(provider) {
  const p = provider || activeProvider();
  const chainId = await p.request({ method: 'eth_chainId' });
  if (String(chainId).toLowerCase() === CHAIN_ID_HEX) return;
  try {
    await p.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: CHAIN_ID_HEX }] });
  } catch (err) {
    // 4902 = the wallet has never heard of this chain. Add it, with exact parameters.
    if (err && (err.code === 4902 || (err.data && err.data.originalError && err.data.originalError.code === 4902))) {
      await p.request({ method: 'wallet_addEthereumChain', params: [CHAIN_PARAMS] });
      return;
    }
    throw err;
  }
}

/** Send a transaction from the connected wallet. Returns the hash. */
export async function sendTx(from, to, fn, args, overrides) {
  const provider = activeProvider();
  if (!provider) throw new Error('No wallet connected.');
  await ensureChain(provider);
  const data = encodeCall(fn, args);
  // Simulate first: a revert surfaces here as a readable message instead of a failed on-chain tx
  // the player pays for. Same discipline as the swap path — simulate before sign.
  await rpc('eth_call', [{ from, to, data }, 'latest']);
  return provider.request({ method: 'eth_sendTransaction', params: [{ from, to, data, ...(overrides || {}) }] });
}

export async function waitForReceipt(hash, timeoutMs) {
  const limit = Date.now() + (timeoutMs || 90000);
  for (;;) {
    try {
      const receipt = await rpc('eth_getTransactionReceipt', [hash]);
      if (receipt) return receipt;
    } catch (e) {
      /* one failed poll is not a failed transaction; keep asking until the window closes */
    }
    if (Date.now() > limit) throw new Error('Timed out waiting for the transaction.');
    await new Promise(r => setTimeout(r, 500));
  }
}

/** Turn a raw revert into something a person can read. */
export function humanError(err) {
  const msg = (err && (err.shortMessage || err.message)) || String(err);
  if (/user rejected|User denied/i.test(msg)) return 'You cancelled the transaction.';
  if (/insufficient funds/i.test(msg)) return 'Not enough ETH for gas on Robinhood Chain.';
  if (/Insufficient\(\)/.test(msg)) return 'The house cannot cover that bet right now. Try a smaller amount.';
  if (/TooSmall\(\)/.test(msg)) return 'That is below the minimum bet.';
  if (/IsPaused\(\)/.test(msg)) return 'The cabinet is closed right now.';
  if (/TooRich\(\)/.test(msg)) return 'That pick pays more than this cabinet will write. Open fewer squares.';
  if (/BadLine\(\)/.test(msg)) return 'That jump line is outside the slider.';
  if (/TooCheap\(\)/.test(msg)) return 'That pick pays 1.00x or less. Open more squares or bury more mines.';
  if (/Bad(Coins|Side|Packets|Mines|Picks)\(\)/.test(msg)) return 'That ticket is outside what this cabinet accepts.';
  if (/transfer amount exceeds balance/i.test(msg)) return 'Not enough MSFT in your wallet.';
  return msg.replace(/^Error: /, '');
}

// ── mobile ───────────────────────────────────────────────────────────────────
//
// Mobile Safari and Chrome have no injected provider, so window.ethereum only exists INSIDE a
// wallet's own in-app browser. The honest mobile flow is therefore "reopen this page inside
// MetaMask", not "install an extension". The deep link below does exactly that.
export function isMobileUA() {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
}

/** A link that opens the current page inside MetaMask's in-app browser. */
export function metamaskDeepLink(path) {
  const host = typeof window !== 'undefined' ? window.location.host : 'internetcoin.vip';
  const p = path || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/');
  return 'https://metamask.app.link/dapp/' + host + p;
}
